import { getConfig } from "@/config";
import { accessTtlMs, isParsedTokenPair, readTokens, writeTokens, type TokenPair } from "./tokens";

export class ApiError extends Error {
  readonly status: number;
  /** The server's own `{"error": "..."}` message, untranslated. */
  readonly serverMessage: string;

  constructor(status: number, serverMessage: string) {
    super(describeStatus(status, serverMessage));
    this.name = "ApiError";
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

/** A Vietnamese, operator-facing description of a failed call. */
export function describeStatus(status: number, serverMessage: string): string {
  switch (status) {
    case 0:
      return "Không kết nối được tới máy chủ";
    case 400:
      return serverMessage ? `Yêu cầu không hợp lệ: ${serverMessage}` : "Yêu cầu không hợp lệ";
    case 401:
      return "Phiên đăng nhập đã hết hạn";
    case 403:
      return serverMessage && !serverMessage.startsWith("missing required permission")
        ? `Không được phép: ${serverMessage}`
        : "Bạn không có quyền thực hiện thao tác này";
    case 404:
      return "Không tìm thấy";
    case 409:
      return serverMessage ? `Xung đột: ${serverMessage}` : "Xung đột dữ liệu";
    case 429:
      return "Quá nhiều yêu cầu, hãy thử lại sau giây lát";
    default:
      return status >= 500 ? `Lỗi máy chủ (${status})${serverMessage ? `: ${serverMessage}` : ""}` : `Lỗi ${status}`;
  }
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Đã xảy ra lỗi không xác định";
}

export function apiUrl(path: string): string {
  return getConfig().apiBaseUrl + path;
}

let sessionExpiredHandler: (() => void) | null = null;

/** Called once the session is definitively gone (refresh failed). */
export function onSessionExpired(fn: (() => void) | null): void {
  sessionExpiredHandler = fn;
}

function expireSession(): void {
  writeTokens(null);
  sessionExpiredHandler?.();
}

// A refresh token is single-use (the server rotates it and treats reuse as
// theft, revoking the whole session). So at most one refresh may be in
// flight: per tab via this promise, across tabs via a Web Lock — inside
// the lock we re-read storage and skip the network call if another tab
// already rotated the pair we were holding.
let inFlight: Promise<TokenPair | null> | null = null;

async function performRefresh(stale: TokenPair): Promise<TokenPair | null> {
  const current = readTokens();
  if (!current) return null;
  if (current.refresh_token !== stale.refresh_token) return current;
  if (Date.parse(current.refresh_expires_at) <= Date.now()) {
    expireSession();
    return null;
  }

  let res: Response;
  try {
    res = await fetch(apiUrl("/api/v1/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: current.refresh_token }),
    });
  } catch {
    throw new ApiError(0, "");
  }
  if (res.ok) {
    const pair: unknown = await res.json();
    if (!isParsedTokenPair(pair)) throw new ApiError(500, "malformed token pair");
    writeTokens(pair);
    return pair;
  }
  if (res.status === 400 || res.status === 401) {
    expireSession();
    return null;
  }
  // 429/5xx: the session may still be fine; keep the tokens.
  throw new ApiError(res.status, await readServerMessage(res));
}

export function refreshTokens(stale: TokenPair): Promise<TokenPair | null> {
  if (inFlight) return inFlight;
  const run = () => performRefresh(stale);
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  inFlight = (locks ? locks.request("omf-token-refresh", run) : run()).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Returns a usable access token, refreshing first if it is (nearly) expired. */
export async function getAccessToken(): Promise<string | null> {
  const t = readTokens();
  if (!t) return null;
  if (accessTtlMs(t) > 10_000) return t.access_token;
  const fresh = await refreshTokens(t);
  return fresh?.access_token ?? null;
}

async function readServerMessage(res: Response): Promise<string> {
  try {
    const text = await res.text();
    if (!text) return "";
    const body: unknown = JSON.parse(text);
    if (body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string") {
      return (body as { error: string }).error;
    }
    return "";
  } catch {
    return "";
  }
}

export interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** Rewrites the raw JSON text before parsing (e.g. to keep int64 IDs exact). */
  transformText?: (text: string) => string;
  /** Skip the Authorization header (public endpoints). */
  anonymous?: boolean;
  /** Return the raw body text instead of parsed JSON (e.g. Prometheus exposition). */
  text?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  let url = apiUrl(path);
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  return url;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: opts.text ? "text/plain" : "application/json" };
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      return await fetch(url, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      throw new ApiError(0, "");
    }
  };

  let token = opts.anonymous ? null : await getAccessToken();
  let res = await send(token);

  if (res.status === 401 && !opts.anonymous) {
    const held = readTokens();
    const fresh = held ? await refreshTokens(held) : null;
    if (!fresh) {
      expireSession();
      throw new ApiError(401, await readServerMessage(res));
    }
    token = fresh.access_token;
    res = await send(token);
    if (res.status === 401) {
      expireSession();
      throw new ApiError(401, await readServerMessage(res));
    }
  }

  if (!res.ok) throw new ApiError(res.status, await readServerMessage(res));
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (opts.text) return text as T;
  if (!text) return undefined as T;
  return JSON.parse(opts.transformText ? opts.transformText(text) : text) as T;
}

export const http = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, { ...opts, body }),
  del: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("DELETE", path, { ...opts, body }),
};
